import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import axios from "axios";
import numberToWords from "../../Accounts/utils/numberToWords";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchStudentFeeStructure } from "../../../api/invoiceApi";

const FeeStructureLetter = ({ student }) => {
  const date = new Date();

  const [formDataFS, setFormDataFS] = useState({ fees: [], total: 0 });
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  const [isEditMode, setIsEditMode] = useState(false);
  const [feeStructureData, setFeeStructureData] = useState("");
  const [saveSpinner, setSaveSpinner] = useState(false);

  const [formData, setFormData] = useState({
    studentName: "",
    fatherName: "",
    address: "",
    course: "",
    academicYear: "",
    duration: "",
    admissionNo: "",
    year1Tuition: "",
    year1Hostel: "",
    year2Tuition: "",
    year2Hostel: "",
    year3Tuition: "",
    year3Hostel: "",
    year4Tuition: "",
    year4Hostel: "",
    totalInWords: "",
  });

  const [totals, setTotals] = useState({ tuition: 0, hostel: 0 });
  const printRef = useRef(null);

  const safeNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const getStudentFeeStructure = async (student) => {
    console.log(student);
    const res = await fetchStudentFeeStructure({
      branch: userData.branch,
      studentId: student.studentId,
      token: userData.token,
      navigate,
    });

    const { data, fees, error } = res;

    const course = student.selectedCourse?.toLowerCase() || "";
    const durationYears =
      course.includes("gnm") || course.includes("g.n.m")
        ? 3
        : course.includes("b.sc")
        ? 4
        : ""; // fallback if unknown

    if (error) {
      setIsEditMode(false);
      setFormData({
        studentName:
          [student.firstName, student.middleName, student.lastName]
            .filter(Boolean)
            .join(" ") || "",
        fatherName: student.fatherName || "",
        address: student.homeAddress || "",
        course: student.selectedCourse || "",
        academicYear: student.academicYear || "",
        duration: durationYears ? `${durationYears} Years` : "",
        admissionNo: student.studentId || "",
        year1Tuition: "",
        year1Hostel: "",
        year2Tuition: "",
        year2Hostel: "",
        year3Tuition: "",
        year3Hostel: "",
        year4Tuition: "",
        year4Hostel: "",
      });
      return;
    }

    setFeeStructureData(() => ({
      ...data,
      fees,
    }));

    if (data.feeLetter) {
      setIsEditMode(true);
      setFormData(data.feeLetter);
      setTotals({
        tuition:
          Number(data.feeLetter.year1Tuition || 0) +
          Number(data.feeLetter.year2Tuition || 0) +
          Number(data.feeLetter.year3Tuition || 0) +
          Number(data.feeLetter.year4Tuition || 0),
        hostel:
          Number(data.feeLetter.year1Hostel || 0) +
          Number(data.feeLetter.year2Hostel || 0) +
          Number(data.feeLetter.year3Hostel || 0) +
          Number(data.feeLetter.year4Hostel || 0),
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...data, // merge data from backend
      studentName:
        [student.firstName, student.middleName, student.lastName]
          .filter(Boolean)
          .join(" ") || "",
      fatherName: student.fatherName || "",
      address: student.homeAddress || "",
      course: student.selectedCourse || "",
      academicYear: student.academicYear || "",
      admissionNo: student.studentId || "",
      duration: durationYears ? `${durationYears} Years` : "",
    }));

    setIsEditMode(true);
  };

  useEffect(() => {
    if (student) {
      console.log(student);
      getStudentFeeStructure(student);
    }
  }, [student]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedForm = { ...formData, [name]: value };
    setFormData(updatedForm);

    if (name.includes("Tuition") || name.includes("Hostel")) {
      const tuition =
        (parseFloat(updatedForm.year1Tuition) || 0) +
        (parseFloat(updatedForm.year2Tuition) || 0) +
        (parseFloat(updatedForm.year3Tuition) || 0) +
        (parseFloat(updatedForm.year4Tuition) || 0);

      const hostel =
        (parseFloat(updatedForm.year1Hostel) || 0) +
        (parseFloat(updatedForm.year2Hostel) || 0) +
        (parseFloat(updatedForm.year3Hostel) || 0) +
        (parseFloat(updatedForm.year4Hostel) || 0);

      setTotals({ tuition, hostel });

      setFormDataFS((prev) => {
        const newFees = [];
        let tuitionTotal = 0;
        let hostelTotal = 0;

        for (let i = 1; i <= 4; i++) {
          const tuitionVal = safeNumber(updatedForm[`year${i}Tuition`]);
          const hostelVal = safeNumber(updatedForm[`year${i}Hostel`]);
          const yearTotal = tuitionVal + hostelVal;

          tuitionTotal += tuitionVal;
          hostelTotal += hostelVal;

          newFees.push({
            description: `${i} Year Fee`,
            amount: yearTotal,
          });
        }

        return {
          ...prev,
          fees: newFees,
          total: tuitionTotal + hostelTotal,
        };
      });
    } else {
      setFormDataFS((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const calculateTotals = () => {
    const tuition =
      (parseFloat(formData.year1Tuition) || 0) +
      (parseFloat(formData.year2Tuition) || 0) +
      (parseFloat(formData.year3Tuition) || 0) +
      (parseFloat(formData.year4Tuition) || 0);
    const hostel =
      (parseFloat(formData.year1Hostel) || 0) +
      (parseFloat(formData.year2Hostel) || 0) +
      (parseFloat(formData.year3Hostel) || 0) +
      (parseFloat(formData.year4Hostel) || 0);
    setTotals({ tuition, hostel });
    return { tuition, hostel };
  };

  const generateCertificate = async () => {
    setSaveSpinner(true);
    calculateTotals();
    const API_BASE = `${process.env.REACT_APP_COLLEGE_ERP_API}api/`;

    const url = isEditMode
      ? `${API_BASE}student-fee?id=${feeStructureData._id}&studentId=${feeStructureData.studentId}&branch=${userData.branch}`
      : `${API_BASE}student-fee?branch=${userData.branch}`;

    const method = isEditMode ? axios.put : axios.post;
    const payload = {
      academicYear: formData.academicYear,
      course: formData.course,
      studentName: formData.studentName,
      studentId: formData.admissionNo,
      ...formDataFS,
      feeLetter: formData,
    };

    try {
      const response = await method(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userData.token}`,
        },
      });

      alert("Fee Structure submitted successfully!");
      // Set edit mode to true so printing is enabled after saving
      setIsEditMode(true);
    } catch (err) {
      console.error("Unexpected error:", err);

      const message = err?.response?.data?.message;
      if (
        message === "Unauthorized" ||
        err?.response?.data?.error?.name === "JsonWebTokenError"
      ) {
        localStorage.removeItem("token");
        navigate("/");
        return;
      }
      if (err?.response?.data?.details?.includes("E11000")) {
        alert("Duplicate entry detected.");
        return;
      }
      alert("Error submitting form.");
    } finally {
      setSaveSpinner(false);
    }
  };

  const generateDurationText = (duration, academicYear) => {
    if (!duration || !academicYear) return "";

    const startYear = parseInt(academicYear.split("-")[0]);
    const durationYears = parseInt(duration);

    if (isNaN(startYear) || isNaN(durationYears)) return "";

    const endYear = startYear + durationYears;
    return `${durationYears} Years (${startYear}-${endYear})`;
  };

  const getCouncilName = (course) => {
    const normalized = (course || "").toLowerCase();
    if (normalized.includes("gnm") || normalized.includes("g.n.m")) {
      return <span>Karnataka Nursing Council Bangalore.</span>;
    }
    return (
      <span>
        Rajiv Gandhi University of Health Science, Karnataka, recognized by
        Govt. of Karnataka, approved by Karnataka Nursing Council, Bangalore and
        Indian Nursing council, New Delhi.
      </span>
    );
  };

  const totalYears =
    formData.course.toLowerCase().includes("gnm") ||
    formData.course.toLowerCase().includes("g.n.m")
      ? 3
      : 4;

  const years = Array.from({ length: totalYears }, (_, i) => i + 1);

  const handlePrint = () => {
    if (!isEditMode) return;

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank");

    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <style>
            @page {
              size: A4;
              margin: 150px 50px 50px 50px;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
            }
            .certificate-container {
              padding: 30px;
              width: 90%;
              margin: auto;
              margin-top: 40px;
            }
            h2 {
              text-align: center;
            }
            pre {
              font-family: inherit;
            }
          </style>
        </head>
        <body onload="window.print(); window.onafterprint = function() { window.close(); }">
          ${printContent}
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      alert("Pop-ups are blocked! Please allow pop-ups for printing.");
    }
  };

  return (
    <Container className="py-4">
      <h3>Fee Structure Letter Entry</h3>
      <Form>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Student Name</Label>
              <Input
                name="studentName"
                value={formData.studentName}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Father's Name</Label>
              <Input
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label>Address</Label>
          <Input
            type="textarea"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </FormGroup>
        <Row>
          <Col md={4}>
            <FormGroup>
              <Label>Course</Label>
              <Input
                name="course"
                value={formData.course}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Academic Year</Label>
              <Input
                type="select"
                name="academicYear"
                value={formData.academicYear}
                onChange={handleChange}
              >
                <option>2023-2024</option>
                <option>2024-2025</option>
                <option>2025-2026</option>
                <option>2026-2027</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label>Duration</Label>
              <Input
                type="select"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
              >
                <option>3 Years</option>
                <option>4 Years</option>
                <option>5 Years</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <FormGroup style={{ width: "300px" }}>
          <Label>Admission No.</Label>
          <Input
            name="admissionNo"
            value={formData.admissionNo}
            onChange={handleChange}
          />
        </FormGroup>

        <h5 className="mt-4">Year-wise Fee Details</h5>
        {years.map((year) => (
          <Row key={year}>
            <Col md={6}>
              <FormGroup>
                <Label>Year {year} Tuition Fee</Label>
                <Input
                  name={`year${year}Tuition`}
                  value={formData[`year${year}Tuition`]}
                  onChange={handleChange}
                  type="text"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Year {year} Hostel Fee</Label>
                <Input
                  name={`year${year}Hostel`}
                  value={formData[`year${year}Hostel`]}
                  onChange={handleChange}
                  type="text"
                />
              </FormGroup>
            </Col>
          </Row>
        ))}

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Total Tuition (Auto)</Label>
              <Input value={totals.tuition.toLocaleString("en-IN")} readOnly />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Total Hostel (Auto)</Label>
              <Input value={totals.hostel.toLocaleString("en-IN")} readOnly />
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label>Total in Words</Label>
          <Input
            name="totalInWords"
            value={numberToWords(totals.tuition + totals.hostel)}
            readOnly
          />
        </FormGroup>

        {saveSpinner ? (
          <Spinner>
            <span></span>
          </Spinner>
        ) : (
          <Button color="primary" onClick={generateCertificate}>
            Save & Generate Certificate
          </Button>
        )}
        
        {/* Disable Print button if fee structure is not saved */}
        <Button 
          color="secondary" 
          onClick={handlePrint} 
          disabled={!isEditMode} 
          className="ml-2"
        >
          Print Certificate
        </Button>
      </Form>

      <div className="mt-5" ref={printRef}>
        <div className="certificate-container">
          <div className="d-flex justify-content-end align-items-center">
            {date.toLocaleString("en-IN", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
          <h2>TO WHOMSOEVER IT MAY CONCERN</h2>
          <p>
            This is to certify that Mr./Ms.{" "}
            <strong>{formData.studentName}</strong>{" "}
            {student?.gender === "Female" ? "D/O" : "S/O"} Mr./Mrs.{" "}
            <strong>{formData.fatherName}</strong>, residing at{" "}
            <strong>{formData.address}</strong>, has been admitted in our
            institution for <strong>{formData.course}</strong> course at
            Rehoboth Institute of Nursing from the academic year{" "}
            <strong>{formData.academicYear}</strong>. on the basis of merit. The
            duration of the course is{" "}
            <strong>
              {generateDurationText(formData?.duration, formData?.academicYear)}
            </strong>
            . Her/His Admission No. is <strong>{formData.admissionNo}</strong>.
          </p>
          <p>
            This institution is affiliated to {getCouncilName(formData.course)}
          </p>
          <p>His/Her fee details are as follows:</p>
          <table
            style={{
              width: "100%",
              marginTop: "20px",
              textAlign: "center",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #000", padding: "8px" }}>
                  YEAR
                </th>
                <th style={{ border: "1px solid #000", padding: "8px" }}>
                  TUITION FEES
                </th>
                <th style={{ border: "1px solid #000", padding: "8px" }}>
                  HOSTEL PER YEAR
                </th>
              </tr>
            </thead>
            <tbody>
              {years.map((year) => (
                <tr key={year}>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    {["I", "II", "III", "IV"][year - 1]}
                  </td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    Rs. {formData[`year${year}Tuition`] || "0"}
                  </td>
                  <td style={{ border: "1px solid #000", padding: "8px" }}>
                    Rs. {formData[`year${year}Hostel`] || "0"}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>TOTAL</strong>
                </td>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>Rs. {totals.tuition.toLocaleString("en-IN")}</strong>
                </td>
                <td style={{ border: "1px solid #000", padding: "8px" }}>
                  <strong>Rs. {totals.hostel.toLocaleString("en-IN")}</strong>
                </td>
              </tr>
              <tr>
                <td
                  colSpan="3"
                  style={{
                    border: "1px solid #000",
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  <strong>
                    GRAND TOTAL: Rs.{" "}
                    {(totals.tuition + totals.hostel).toLocaleString("en-IN")}{" "}
                    /- ({numberToWords(totals.tuition + totals.hostel)})
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
          <p style={{ marginTop: "50px", textAlign: "right" }}>
            <strong>Principal / Director</strong>
          </p>
          <h5 className="mt-0 pt-0">
            Bank Details for Fee Payment - ( Fee payment may be made through
            NEFT)
          </h5>

          <div className="mt-0 pt-0">
            Name of the current account: Rehoboth Institute of Nursing
            <br />
            Name of Bank and Branch: FEDERAL BANK, Thanisandra, Bangalore
            <br />
            Account No: 23880200000311
            <br />
            IFSC Code: FDRL0002388
          </div>
        </div>
      </div>
    </Container>
  );
};

export default FeeStructureLetter;